import { useState } from "react";
import type { Rec, Finding, Pace } from "@shared/types.js";
import { computeStats } from "@shared/stats.js";
import { fnum, fpct } from "../lib/format.js";
import { COMP_ORDER, COMP_LABEL, compRows } from "../lib/comps.js";
import { DonutCard, BarRow, Kpi } from "../components/Charts.js";
import { ComponentTable } from "../components/ComponentTable.js";
import { Funnel } from "../components/Funnel.js";
import { PaceCard } from "../components/PaceCard.js";

export function Overview({
  rows, findings, pace, targetDate, prevCompletion,
  onSetTarget, onOpenCrosscheck, onOpenWorklist, onPickStatus,
}: {
  rows: Rec[];
  findings: Finding[];
  pace: Pace;
  targetDate: string | null;
  prevCompletion: number | null;
  onSetTarget: (d: string | null) => void;
  onOpenCrosscheck: () => void;
  onOpenWorklist: () => void;
  onPickStatus: (status: string) => void;
}) {
  const s = computeStats(rows);
  const high = findings.filter((f) => f.severity === "high").length;
  const med = findings.filter((f) => f.severity === "medium").length;
  const [showIdle, setShowIdle] = useState(false);

  /* Components where nothing has been recorded at all - folded away by
     default so they do not take the same room as components with progress. */
  const live: typeof COMP_ORDER = [];
  const idle: typeof COMP_ORDER = [];
  let idleRows = 0;
  for (const c of COMP_ORDER) {
    const cr = compRows(rows, c);
    if (!cr.length) continue;
    const touched = cr.some((r) => r.received || r.nrDone || r.estDone || r.ccDate);
    if (touched) live.push(c);
    else { idle.push(c); idleRows += cr.length; }
  }

  return (
    <>
      <div className="sec">
        <div className="sec-head">
          <span className="eyebrow">Section 1</span>
          <h2><i className="bi bi-speedometer2 hico" aria-hidden="true" />Where we stand</h2>
          <span className="note">
            {fnum(s.total)} records in scope &middot; every figure recalculates with the filters above
          </span>
        </div>

        <PaceCard
          s={s} pace={pace} targetDate={targetDate}
          prevCompletion={prevCompletion} onSetTarget={onSetTarget}
        />

        <div className="grid g-kpi" style={{ marginTop: 12 }}>
          <Kpi icon="bi-lightning-charge" label="Ready to price now" value={fnum(s.awaiting)} tone="await"
            foot={<>CR in hand, nothing entered &mdash;{" "}
              <button className="linkbtn" type="button" onClick={onOpenWorklist}>open worklist</button></>} />
          <Kpi icon="bi-hourglass-split" label="Awaiting an estimate" value={fnum(s.estOutstanding)} tone="none"
            foot={<><span className="pct">{fpct(s.estOutstanding, s.total)}</span> of records in scope</>} />
          <Kpi icon="bi-eye" label="Entered, not reviewed" value={fnum(Math.max(0, s.entered - s.crosschecked))} tone="est"
            foot="Waiting on a crosscheck" />
          <Kpi icon="bi-exclamation-triangle" label="Data-quality findings" value={fnum(findings.length)} tone={high ? "alert" : "none"}
            foot={<>{fnum(high)} high, {fnum(med)} medium &mdash;{" "}
              <button className="linkbtn" type="button" onClick={onOpenCrosscheck}>review</button></>} />
        </div>
      </div>

      <div className="sec">
        <div className="sec-head">
          <h2><i className="bi bi-funnel hico" aria-hidden="true" />The pipeline</h2>
          <span className="note">
            Every bar is a share of the same {fnum(s.total)} records, so the drop between stages is the story
          </span>
        </div>
        <div className="card">
          <Funnel s={s} onPick={onPickStatus} />
          <div className="tblnote">
            Records can skip a stage &mdash; an estimate counts as &ldquo;rate entered&rdquo; without a
            CR ever being received, which is why that bar can exceed the one above it.
          </div>
        </div>
      </div>

      <div className="sec">
        <div className="sec-head">
          <h2><i className="bi bi-pie-chart hico" aria-hidden="true" />Status mix</h2>
          <span className="note">Four mutually exclusive buckets that sum to the total</span>
        </div>
        <div className="card">
          <div className="grid g-donut" style={{ padding: 6 }}>
            <DonutCard
              title="Overall entry progress" subtitle="every record counted once"
              centerValue={`${s.completion.toFixed(0)}%`} centerLabel="complete"
              segments={[
                { label: "Priced (new rate)", value: s.xPriced, color: "var(--s-done)" },
                { label: "Estimated only", value: s.xEstOnly, color: "var(--s-est)" },
                { label: "Received, awaiting pricing", value: s.xAwaiting, color: "var(--s-await)" },
                { label: "Nothing entered yet", value: s.xNone, color: "var(--s-none)" },
              ]}
            />
            <div style={{ flex: 1, minWidth: 260, padding: "14px 10px" }}>
              <div className="bars" style={{ padding: 0 }}>
                {live.map((c) => {
                  const cs = computeStats(compRows(rows, c));
                  return (
                    <BarRow
                      key={c}
                      label={COMP_LABEL[c]}
                      sub={`${fnum(cs.total)} ${cs.total === 1 ? "record" : "records"}`}
                      totalTxt={<><b>{cs.completion.toFixed(0)}%</b> done</>}
                      segments={[
                        { label: "Priced", value: cs.xPriced, color: "var(--s-done)" },
                        { label: "Estimated only", value: cs.xEstOnly, color: "var(--s-est)" },
                        { label: "Awaiting pricing", value: cs.xAwaiting, color: "var(--s-await)" },
                        { label: "Outstanding", value: cs.xNone, color: "var(--s-none)" },
                      ]}
                    />
                  );
                })}
              </div>

              {idle.length > 0 && (
                <div className="idlefold">
                  <button className="linkbtn" type="button" onClick={() => setShowIdle((v) => !v)}>
                    {showIdle ? "▾" : "▸"} {fnum(idleRows)} records untouched across{" "}
                    {idle.map((c) => COMP_LABEL[c]).join(", ")}
                  </button>
                  {showIdle && (
                    <div className="bars" style={{ padding: "8px 0 0" }}>
                      {idle.map((c) => {
                        const cs = computeStats(compRows(rows, c));
                        return (
                          <BarRow
                            key={c} label={COMP_LABEL[c]}
                            sub={`${fnum(cs.total)} ${cs.total === 1 ? "record" : "records"}`}
                            totalTxt={<><b>0%</b> done</>}
                            segments={[{ label: "Outstanding", value: cs.total, color: "var(--s-none)" }]}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="tblnote">
            <span className="pill p-done">Priced (new rate)</span>{" "}
            <span className="pill p-est">Estimated</span>{" "}
            <span className="pill p-await">Received, awaiting pricing</span>{" "}
            <span className="pill p-none">Outstanding / not received</span>
          </div>
        </div>
      </div>

      <ComponentTable rows={rows} title="All components — summary" />
    </>
  );
}
